import { html } from "lit-html";
import { useState } from "Shared/haunted/CustomHooks";
import classNames from "classnames";
import { JetSmartEvent } from "Services/eventbus/JetSmartEvent";
import { Tab, TabProps } from "../../tabs";
import { useHotelsTab } from "./hotels/useHotelsTab";
import { useInsuranceTab, showInsuranceTab } from "./insurance/useInsuranceTab";
import { useCarsTab } from "./cars/useCarsTab";
import { useTransfersTab } from "./transfers/useTransfersTab";
import { useFlightsTab } from "./flights/useFlightsTab";
import { useReduxState } from "Shared/redux/useReduxState";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
}

export const TAB_NAMES = ["flights", "hotels", "cars", "transfers", "insurance"] as const;
export type TabName = (typeof TAB_NAMES)[number];

export const useSmartSearchTab = (props: Props) => {
	const [userInfo] = useReduxState("userInfo");
	const [kontent] = useReduxState("kontent");

	const [loaderElemId] = useState(`flights-tab-${Date.now().toString()}`);
	const [selectedTab, setSelectedTab] = useState<TabName>("flights");

	const carsTab = useCarsTab({ culture: props.culture });
	const flightsTab = useFlightsTab({
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		loaderElemId,
	});
	const hotelsTab = useHotelsTab({ culture: props.culture });
	const insuranceTab = useInsuranceTab({ culture: props.culture, dynamicSettings: props.dynamicSettings });
	const transfersTab = useTransfersTab({ culture: props.culture });

	const raiseEventBusEvent = (id: string) => {
		window.eventBus.raiseEvent({
			name: JetSmartEvent.SearchBoxTabClick,
			params: { id, event: "click", data: {} },
		});
	};

	const tabHeaderTemplate = (isSelected: boolean, label: string, icon: string) => html`
		<div
			class=${classNames(
				"rounded-sm flex items-center text-sm/none px-1 py-2",
				"md:text-base/none md:px-3 md:py-4 max-w-fit md:rounded-xl md:cursor-pointer",
				{
					"bg-[#f7f7f7] text-[#7D93B2] md:hover:bg-[#F0F3F7]": !isSelected,
					"bg-n-cyan text-white": isSelected,
				}
			)}
		>
			<span class="mr-2 hidden sm:inline-block">${unsafeHTML(icon)}</span>
			<span> ${unsafeHTML(label)} </span>
		</div>
	`;

	const tabs = (): Tab<TabName>[] => {
		const allTabs: Tab<TabName>[] = [
			{
				name: "flights",
				contentTemplate: flightsTab.htmlTemplate,
				tabHeaderTemplate: () =>
					tabHeaderTemplate(
						selectedTab === "flights",
						kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionTitle.value,
						kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionIcon.value
					),
				onClick: () => raiseEventBusEvent("Searchbox.flightsTab"),
			},
			{
				name: "hotels",
				contentTemplate: hotelsTab.htmlTemplate,
				tabHeaderTemplate: () =>
					tabHeaderTemplate(
						selectedTab === "hotels",
						kontent?.searchbox.searchboxHotelsSection.contents.searchboxSectionTitle.value,
						kontent?.searchbox.searchboxHotelsSection.contents.searchboxSectionIcon.value
					),
				onClick: () => raiseEventBusEvent("Searchbox.hotelsTab"),
			},
			{
				name: "cars",
				contentTemplate: carsTab.htmlTemplate,
				tabHeaderTemplate: () =>
					tabHeaderTemplate(
						selectedTab === "cars",
						kontent?.searchbox.searchboxCarsSection.contents.searchboxSectionTitle.value,
						kontent?.searchbox.searchboxCarsSection.contents.searchboxSectionIcon.value
					),
				onClick: () => raiseEventBusEvent("Searchbox.rentalcarsTab"),
			},
			{
				name: "transfers",
				contentTemplate: transfersTab.htmlTemplate,
				tabHeaderTemplate: () =>
					tabHeaderTemplate(
						selectedTab === "transfers",
						kontent?.searchbox.searchboxTransferSection.contents.searchboxSectionTitle.value,
						kontent?.searchbox.searchboxTransferSection.contents.searchboxSectionIcon.value
					),
				onClick: () => raiseEventBusEvent("Searchbox.transfersTab"),
			},
			{
				name: "insurance",
				contentTemplate: insuranceTab.htmlTemplate,
				tabHeaderTemplate: () =>
					tabHeaderTemplate(
						selectedTab === "insurance",
						kontent?.searchbox.searchboxInsuranceSection.contents.searchboxSectionTitle.value,
						kontent?.searchbox.searchboxInsuranceSection.contents.searchboxSectionIcon.value
					),
				onClick: () => raiseEventBusEvent("Searchbox.insuranceTab"),
			},
		];

		return allTabs.filter((tab) => {
			if (userInfo?.ChileCompra?.IsMember) return tab.name === "flights";
			if (tab.name === "insurance") return !!showInsuranceTab(props.dynamicSettings, props.culture);
			if (tab.name === "hotels") return props.dynamicSettings.isHotelsOn;
			if (tab.name === "cars") return props.dynamicSettings.IsCarRentalOn;
			if (tab.name === "transfers") return props.dynamicSettings.IsTransfersOn;
			return true;
		});
	};

	const tabsTemplate = () => {
		const tabProps: TabProps<TabName> = { data: tabs(), selectedTab, onSelect: setSelectedTab };
		return html` <dc-tabs class="m-4 w-full px-4" .dto=${tabProps}> </dc-tabs> `;
	};

	const htmlTemplate = () => html`
		<div id=${loaderElemId} class="w-full">
			<div class="flex w-full justify-center">${tabsTemplate()}</div>
		</div>
	`;

	return { htmlTemplate };
};
